/**
 * Content calendar: month tabs, copy to clipboard, ZIP download-all, modal.
 */
class ContentCalendarController {
	/**
	 * @param {HTMLElement} root
	 */
	constructor( root ) {
		this.root = root;
		this.onTabClick = this.onTabClick.bind( this );
		this.onCopyClick = this.onCopyClick.bind( this );
		this.onDownloadAll = this.onDownloadAll.bind( this );
		this.onMediaClick = this.onMediaClick.bind( this );
		this.onModalClose = this.onModalClose.bind( this );
		this.onModalKeydown = this.onModalKeydown.bind( this );
		this.init();
	}

	init() {
		this.tabs = Array.from( this.root.querySelectorAll( '.content-calendar__month-tab' ) );
		this.panels = Array.from( this.root.querySelectorAll( '.content-calendar__month' ) );
		this.tabs.forEach( ( tab ) => tab.addEventListener( 'click', this.onTabClick ) );
		this.root.querySelectorAll( '.content-calendar-card__copy' ).forEach( ( btn ) => {
			btn.addEventListener( 'click', this.onCopyClick );
		} );
		this.root.querySelectorAll( '.content-calendar__download-all' ).forEach( ( btn ) => {
			btn.addEventListener( 'click', this.onDownloadAll );
		} );

		this.root.querySelectorAll( '.content-calendar-card__media-trigger' ).forEach( ( btn ) => {
			btn.addEventListener( 'click', this.onMediaClick );
		} );

		this.modal = this.root.querySelector( '.content-calendar-modal' );
		if ( this.modal ) {
			this.modalPanel = this.modal.querySelector( '.content-calendar-modal__panel' );
			this.modalImage = this.modal.querySelector( '.content-calendar-modal__image' );
			this.modalTitle = this.modal.querySelector( '.content-calendar-modal__title' );
			this.modalCopy = this.modal.querySelector( '.content-calendar-modal__copy' );
			this.modalDate = this.modal.querySelector( '.content-calendar-modal__date' );
			this.modalDownload = this.modal.querySelector( '.content-calendar-modal__download' );
			this.modalResourceLink = this.modal.querySelector( '.content-calendar-modal__resource-link' );
			this.modalCopyBtn = this.modal.querySelector( '[data-cc-modal-copy-btn]' );
			this.modalShareX = this.modal.querySelector( '.content-calendar-modal__share-link--x' );
			this.modalShareFacebook = this.modal.querySelector( '.content-calendar-modal__share-link--facebook' );
			this.modal.querySelectorAll( '[data-cc-modal-close]' ).forEach( ( el ) => {
				el.addEventListener( 'click', this.onModalClose );
			} );
		}

		this.openFromDataAttribute();
	}

	openFromDataAttribute() {
		if ( ! this.modal ) {
			return;
		}
		const raw = this.root.getAttribute( 'data-cc-open-social-resource-id' ) || '';
		const id = parseInt( raw, 10 ) || 0;
		if ( ! id ) {
			return;
		}

		const trigger = this.root.querySelector(
			`.content-calendar-card__media-trigger[data-cc-social-resource-id="${ id }"]`
		);
		if ( trigger instanceof HTMLElement ) {
			trigger.click();
		}
	}

	/**
	 * @param {MouseEvent} event
	 */
	onTabClick( event ) {
		const tab = event.currentTarget;
		const idx = tab.getAttribute( 'data-month-index' );
		if ( idx === null ) {
			return;
		}
		this.tabs.forEach( ( t ) => {
			const active = t === tab;
			t.classList.toggle( 'is-active', active );
			t.setAttribute( 'aria-selected', active ? 'true' : 'false' );
		} );
		this.panels.forEach( ( panel ) => {
			const match = panel.getAttribute( 'data-month-index' ) === idx;
			panel.toggleAttribute( 'hidden', ! match );
			panel.classList.toggle( 'content-calendar__month--active', match );
		} );
	}

	/**
	 * @param {MouseEvent} event
	 */
	onCopyClick( event ) {
		const btn = event.currentTarget;
		const targetId = btn.getAttribute( 'data-copy-target' );
		if ( ! targetId ) {
			return;
		}
		const source = document.getElementById( targetId );
		if ( ! source || ! ( source instanceof HTMLTextAreaElement ) ) {
			return;
		}
		const text = source.value || '';
		if ( navigator.clipboard?.writeText ) {
			navigator.clipboard.writeText( text ).catch( () => {} );
		}
	}

	/**
	 * @param {MouseEvent} event
	 */
	async onDownloadAll( event ) {
		const btn = event.currentTarget;
		const raw = btn.getAttribute( 'data-download-attachment-ids' ) || '[]';
		let ids = [];
		try {
			ids = JSON.parse( raw );
		} catch {
			return;
		}
		if ( ! Array.isArray( ids ) || ! ids.length ) {
			return;
		}

		const endpoint = this.root.getAttribute( 'data-wdg-cc-zip-endpoint' );
		const nonce = this.root.getAttribute( 'data-wdg-cc-rest-nonce' );
		const filename = btn.getAttribute( 'data-download-zip-filename' ) || 'content-calendar-downloads';

		if ( endpoint ) {
			try {
				const headers = {
					'Content-Type': 'application/json',
				};
				if ( nonce ) {
					headers[ 'X-WP-Nonce' ] = nonce;
				}

				const res = await fetch( endpoint, {
					method: 'POST',
					credentials: 'same-origin',
					headers,
					body: JSON.stringify( {
						attachment_ids: ids.map( ( n ) => parseInt( n, 10 ) ).filter( ( n ) => n > 0 ),
						filename,
					} ),
				} );

				const contentType = ( res.headers.get( 'content-type' ) || '' ).toLowerCase();
				if ( ! res.ok || contentType.includes( 'application/json' ) ) {
					let msg = 'Zip request failed';
					try {
						const data = await res.json();
						msg = data?.message || data?.code || msg;
					} catch {
						// ignore.
					}
					throw new Error( msg );
				}
				const blob = await res.blob();
				const url = URL.createObjectURL( blob );
				const a = document.createElement( 'a' );
				a.href = url;
				a.download = filename.toLowerCase().endsWith( '.zip' ) ? filename : `${ filename }.zip`;
				a.style.display = 'none';
				document.body.appendChild( a );
				a.click();
				a.remove();
				URL.revokeObjectURL( url );
				return;
			} catch {
				// ZIP unavailable or blocked; no safe client fallback for attachment IDs.
			}
		}
	}

	/**
	 * @param {MouseEvent} event
	 */
	onMediaClick( event ) {
		if ( ! this.modal ) {
			return;
		}

		const btn = event.currentTarget;
		this.lastFocused = btn instanceof HTMLElement ? btn : null;

		const title = btn.getAttribute( 'data-cc-modal-title' ) || '';
		const copy = btn.getAttribute( 'data-cc-modal-copy' ) || '';
		const date = btn.getAttribute( 'data-cc-modal-date' ) || '';
		const image = btn.getAttribute( 'data-cc-modal-image' ) || '';
		const assetType = btn.getAttribute( 'data-cc-asset-type' ) || 'image';
		const assetUrl = btn.getAttribute( 'data-cc-modal-asset-url' ) || '';
		const resourceUrl = btn.getAttribute( 'data-cc-modal-resource-url' ) || '';
		const downloadUrl = btn.getAttribute( 'data-cc-modal-download-url' ) || '';
		const shareX = btn.getAttribute( 'data-cc-modal-share-x' ) || '';
		const shareFacebook = btn.getAttribute( 'data-cc-modal-share-facebook' ) || '';

		if ( this.modalTitle ) this.modalTitle.textContent = title;
		if ( this.modalCopy ) this.modalCopy.textContent = copy;
		if ( this.modalDate ) this.modalDate.textContent = date;

		if ( this.modalImage ) {
			const mediaContainer = this.modalImage.parentElement;
			// Remove any video left over from a previous open.
			const prevVideo = mediaContainer ? mediaContainer.querySelector( 'video' ) : null;
			if ( prevVideo ) {
				prevVideo.pause();
				prevVideo.remove();
			}

			if ( assetType === 'video' && assetUrl ) {
				this.modalImage.setAttribute( 'hidden', '' );
				const video = document.createElement( 'video' );
				video.src = assetUrl;
				video.controls = true;
				video.autoplay = true;
				video.className = 'content-calendar-modal__video';
				if ( mediaContainer ) {
					mediaContainer.appendChild( video );
				}
			} else {
				this.modalImage.src = image || '';
				this.modalImage.alt = title || '';
				this.modalImage.toggleAttribute( 'hidden', ! image );
			}
		}

		if ( this.modalDownload ) {
			this.modalDownload.href = downloadUrl || '#';
			this.modalDownload.setAttribute( 'aria-disabled', downloadUrl ? 'false' : 'true' );
			this.modalDownload.classList.toggle( 'is-disabled', ! downloadUrl );
		}

		if ( this.modalResourceLink ) {
			this.modalResourceLink.href = resourceUrl || '#';
			this.modalResourceLink.textContent = resourceUrl || '';
			const adminWrap = this.modal.querySelector( '[data-cc-modal-admin]' );
			if ( adminWrap ) {
				adminWrap.hidden = ! resourceUrl;
			}
		}

		if ( this.modalShareX ) {
			this.modalShareX.href = shareX || '#';
		}
		if ( this.modalShareFacebook ) {
			this.modalShareFacebook.href = shareFacebook || '#';
		}

		if ( this.modalCopyBtn ) {
			this.modalCopyBtn.onclick = () => {
				if ( navigator.clipboard?.writeText ) {
					navigator.clipboard.writeText( copy ).catch( () => {} );
				}
			};
		}

		this.modal.hidden = false;
		document.addEventListener( 'keydown', this.onModalKeydown );

		const focusTarget = this.modal.querySelector( '[data-cc-modal-close]' );
		if ( focusTarget instanceof HTMLElement ) {
			focusTarget.focus();
		}
	}

	onModalClose() {
		if ( ! this.modal ) {
			return;
		}
		// Stop + remove any dynamically added video.
		const video = this.modal.querySelector( 'video' );
		if ( video ) {
			video.pause();
			video.remove();
		}
		if ( this.modalImage ) {
			this.modalImage.removeAttribute( 'hidden' );
		}
		this.modal.hidden = true;
		document.removeEventListener( 'keydown', this.onModalKeydown );
		if ( this.lastFocused instanceof HTMLElement ) {
			this.lastFocused.focus();
		}
	}

	/**
	 * @param {KeyboardEvent} event
	 */
	onModalKeydown( event ) {
		if ( event.key === 'Escape' ) {
			event.preventDefault();
			this.onModalClose();
		}
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.content-calendar' ).forEach( ( el ) => {
		new ContentCalendarController( el );
	} );
} );
